// validators/object.ts
import { assertValidation, ValidationErrorBuilder } from "../errors.mjs";
import { registry } from "../registry.mjs";
import { AbstractValidator, makeValidatorFactory } from "../types.mjs";
import { getValidator } from "../utilities.mjs";
var AnyObjectValidator = class extends AbstractValidator {
  validate(value) {
    assertValidation(typeof value === "object", 'Value is not an "object"');
    assertValidation(value !== null, 'Value is "null"');
    return value;
  }
};
var ObjectValidator = class extends AbstractValidator {
  schema;
  validators = /* @__PURE__ */ new Map();
  additionalProperties;
  constructor(schema) {
    super();
    const { [Symbol.justusAdditionalValidator]: additional, ...properties } = schema;
    if (additional) this.additionalProperties = additional;
    for (const key of Object.keys(properties)) {
      this.validators.set(key, getValidator(properties[key]));
    }
    this.schema = schema;
  }
  validate(value, options = {}) {
    assertValidation(typeof value === "object", 'Value is not an "object"');
    assertValidation(value !== null, 'Value is "null"');
    const { stripAdditionalProperties, stripOptionalNulls, partialValidation } = options;
    const record = value;
    const builder = new ValidationErrorBuilder();
    const clone = {};
    for (const [key, validator] of this.validators.entries()) {
      const optional = !!validator.optional || !!partialValidation;
      const original = record[key];
      if (stripOptionalNulls && optional && original === null) {
        continue;
      }
      if (original === void 0) {
        if (partialValidation) continue;
        try {
          const validated = validator.validate(original, options);
          if (!(optional && validated === void 0)) clone[key] = validated;
        } catch {
          if (optional) continue;
          builder.record("Required property missing", key);
        }
        continue;
      }
      try {
        const validated = validator.validate(original, options);
        if (!(optional && validated === void 0)) clone[key] = validated;
      } catch (error) {
        builder.record(error, key);
      }
    }
    const additionalKeys = Object.keys(record).filter((k) => !this.validators.has(k));
    const additional = this.additionalProperties;
    if (additional) {
      additionalKeys.forEach((key) => {
        if (record[key] === void 0) return;
        try {
          clone[key] = additional.validate(record[key], options);
        } catch (error) {
          builder.record(error, key);
        }
      });
    } else if (!stripAdditionalProperties) {
      additionalKeys.forEach((key) => {
        if (record[key] !== void 0) builder.record("Unknown property", key);
      });
    }
    return builder.assert(clone);
  }
};
function objectValidatorFactory(schema) {
  const validator = new ObjectValidator(schema);
  function* iterator() {
    yield { [Symbol.justusRestValidator]: validator };
  }
  return Object.defineProperties(schema, {
    [Symbol.justusValidator]: { value: validator, enumerable: false },
    [Symbol.iterator]: { value: iterator, enumerable: false }
  });
}
var object = makeValidatorFactory(new AnyObjectValidator(), objectValidatorFactory);
function objectOf(validation) {
  return new ObjectValidator({ [Symbol.justusAdditionalValidator]: getValidator(validation) });
}
registry.set("object", ObjectValidator);
export {
  AnyObjectValidator,
  ObjectValidator,
  object,
  objectOf,
  objectValidatorFactory
};
//# sourceMappingURL=object.mjs.map
